import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./modal.module.scss";
import { Portal } from "../Portal/Portal";

interface ModalProps {
  className?: string;
  children?: ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  customStyles?: string;
  blockClose?: boolean;
}

const ANIMATION_DELAY = 150;

export const Modal = (props: ModalProps) => {
  const { children, isOpen, onClose, customStyles, blockClose } = props;

  const [isClosing, setIsClosing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const contentRef = useRef<HTMLDivElement>(null);

  const closeHandler = useCallback(() => {
    if (onClose && !blockClose) {
      setIsClosing(true);
      timerRef.current = setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, ANIMATION_DELAY);
    }
  }, [onClose, blockClose]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeHandler();
      }
    },
    [closeHandler],
  );

  const onContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (isOpen) {
      contentRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("keydown", onKeyDown);
    }

    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onKeyDown]);

  return (
    <Portal>
      <div
        className={`${styles.Modal} ${isOpen && styles.opened} ${
          isClosing && styles.isClosing
        } ${customStyles} `}
      >
        <div className={styles.overlay} onClick={closeHandler}>
          <div
            ref={contentRef}
            tabIndex={-1}
            className={`${styles.content} comment_content_wrap`}
            onClick={onContentClick}
          >
            {onClose && (
              <button
                className={styles.close_button}
                type="button"
                onClick={closeHandler}
              >
                &times;
              </button>
            )}
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
};
